import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';
import Navigation from './components/Navigation';
import ProfileOnboarding from './components/ProfileOnboarding';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Home from './pages/Home';
import Issues from './pages/Issues';
import NewIssue from './pages/NewIssue';
import IssueDetail from './pages/IssueDetail';
import Leaders from './pages/Leaders';
import LeaderDetail from './pages/LeaderDetail';
import Trending from './pages/Trending';
import Profile from './pages/Profile';
import PostDetail from './pages/PostDetail';
import AdDetail from './pages/AdDetail';
import AdminDashboard from './pages/AdminDashboard';
import Analytics from './pages/Analytics';
import CreateRoom from './pages/CreateRoom';
import Room from './pages/Room';
import RoomResult from './pages/RoomResult';
import DebateReplay from './pages/DebateReplay';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Guidelines from './pages/Guidelines';
import Contact from './pages/Contact';
import './App.css';

// Redirect component for legacy politician routes
function PoliticianRedirect() {
  const { id } = useParams();
  return <Navigate to={`/leaders/${id}`} replace />;
}

// Onboarding Modal Wrapper - renders at app level with error boundary
function OnboardingWrapper() {
  const { user, showOnboarding, setShowOnboarding, onOnboardingComplete } = useAuth();
  
  if (!user || !showOnboarding) return null;
  
  return (
    <ErrorBoundary errorMessage="Unable to load profile setup. Please try again.">
      <ProfileOnboarding
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={onOnboardingComplete}
        user={user}
      />
    </ErrorBoundary>
  );
}

function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment (not query params) for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Hide nav on login, auth callback, and room pages (immersive experience)
  const hideNavPaths = ['/login', '/auth/callback'];
  const isRoomPage = location.pathname.startsWith('/rooms/') && location.pathname !== '/rooms/create';
  const showNav = !hideNavPaths.includes(location.pathname) && !isRoomPage;

  return (
    <>
      {showNav && <Navigation />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* Public routes - view only */}
        <Route path="/" element={<Home />} />
        <Route path="/issues" element={<Issues />} />
        <Route path="/issues/:id" element={<IssueDetail />} />
        <Route path="/leaders" element={<Leaders />} />
        <Route path="/leaders/:id" element={<LeaderDetail />} />
        <Route path="/rooms/:roomId/replay" element={<DebateReplay />} />
        {/* Legacy routes - redirect to Leaders pages */}
        <Route path="/politicians" element={<Navigate to="/leaders" replace />} />
        <Route path="/politicians/:id" element={<PoliticianRedirect />} />
        <Route
          path="/issues/new"
          element={
            <ProtectedRoute>
              <NewIssue />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trending"
          element={
            <ProtectedRoute>
              <Trending />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:id"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          }
        />
        {/* Room/Debate Routes */}
        <Route
          path="/rooms/create"
          element={<CreateRoom />}
        />
        <Route
          path="/rooms/:roomId"
          element={
            <ProtectedRoute>
              <Room />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rooms/:roomId/result"
          element={
            <ProtectedRoute>
              <RoomResult />
            </ProtectedRoute>
          }
        />
        {/* Social Posts - Public view */}
        <Route path="/posts/:postId" element={<PostDetail />} />
        {/* Ads - Public view */}
        <Route path="/ads/:adId" element={<AdDetail />} />
        {/* Legal & Informational Pages - Public */}
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/guidelines" element={<Guidelines />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary 
          errorMessage="Something went wrong loading this page. Please try reloading."
        >
          <AppRouter />
        </ErrorBoundary>
        <OnboardingWrapper />
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
